// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// wire.ts — everything that crosses the network, in one small file, so the whole protocol can be
// read in thirty seconds and the channel-name budget can be asserted in a test.
//
// Salient is LOCKSTEP: the battle is a pure function of (seed, mode, roster, command log), so the
// only thing on the wire is the command log. Nothing about unit positions, hit points or deaths is
// ever transmitted — which is exactly why src/sim.ts may only use exactly-specified arithmetic.
//
// EVERY MESSAGE CARRIES THE WHOLE LOG, not the latest command. A three-minute battle is at most a
// couple of hundred commands of four small integers each, so the wire cost is nothing, and in
// exchange a dropped datagram becomes unobservable: the next message reconstructs the world. The
// classic lockstep hang — "peer A's order for tick 400 was lost, and now both peers wait for each
// other for ever" — cannot occur, because there is no delta to lose.

/** Trystero action names must be <= 12 bytes; net.ts throws in dev if they are not. */
export const CHANNELS = {
  /** The whole command log, plus a digest and the sender's tick. Also the 2s heartbeat. */
  orders: 'ord',
} as const;

/** One command, as it travels. Four small integers and nothing else. */
export interface Wire {
  /** Round number of the MATCH, so a stale message from the previous round cannot disturb us. */
  r: number;
  /** The whole command log, flattened — see src/commands.ts for the packing. */
  l: number[];
  /** The sender's simulation tick, so a peer can tell how far behind it is running. */
  t: number;
  /** FNV digest of the state at the sender's last checkpoint, and the tick it was taken at. */
  d: number;
  dt: number;
  /** Rules revision — a mismatch is a stale cached build, not a desync, and says so. */
  v: number;
}
